import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { Typography } from '@mui/material';
import { Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DataGridPremium as DataGrid, GridColDef } from '@mui/x-data-grid-premium';
import {
  DocumentTag_CreateDocumentTagInput,
  DocumentTag_FieldsFragment,
  DocumentTag_UpdateDocumentTagInput,
  useDocumentTag_CreateDocumentTagMutation,
  useDocumentTag_GetAllDocumentTagsQuery,
  useDocumentTag_UpdateDocumentTagMutation,
} from 'queries';
import { ChevronRight } from 'ts-react-feather-icons';

import HomeLayout from 'layouts/home-layout';
import DocumentTagPill from 'pages/common/document/document-tag-pill';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import CreateDocumentTagModal from './components/create-document-tag-modal';
import { linkedFieldOptions } from './components/document-tag-form';
import EditDocumentTagModal from './components/edit-document-tag-modal';

const useStyles = makeStyles((theme) => ({
  root: {
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
    '&.MuiDataGrid-root .MuiDataGrid-columnHeaderTitleContainer': {
      paddingRight: 0,
      paddingLeft: 0,
    },
    width: '100%',
  },
  container: {
    padding: '5%',
    flexGrow: 1,
  },
  title: {
    fontWeight: 600,
  },
  routeIconContainer: {
    verticalAlign: 'middle',
  },
  button: {
    backgroundColor: '#5ED1F0',
    fontSize: '12px',
    color: '#1C2541',
    margin: theme.spacing(2, 0),
  },
  label: {
    textTransform: 'capitalize',
  },
}));

const SuperAdminDocumentTags = () => {
  const classes = useStyles();
  const history = useHistory();
  const [isAddDocumentTagModalOpen, setIsAddDocumentTagModalOpen] = useState(false);
  const [editingDocumentTag, setEditingDocumentTag] = useState<DocumentTag_FieldsFragment | null>(
    null,
  );

  const {
    data: documentTagsData,
    loading: loadingDocumentTags,
    refetch: refetchDocumentTags,
  } = useDocumentTag_GetAllDocumentTagsQuery({
    onError: (error) => {
      showAlert({
        message: 'Error getting all document tags: ' + error.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const columns = useMemo(() => {
    const gridColumns: GridColDef<DocumentTag_FieldsFragment>[] = [
      {
        disableColumnMenu: true,
        field: 'name',
        headerName: 'Tag',
        flex: 2,

        renderCell: ({ row }) => {
          return <DocumentTagPill documentTag={row} />;
        },
        valueGetter: ({ row }) => row.name,
      },
      {
        field: 'category',
        headerName: 'Category',
        flex: 1,
        renderCell: ({ row }) => (
          <div className={classes.label}>{row.category?.replace('_', ' ')}</div>
        ),
      },
      {
        field: 'linkedField',
        headerName: 'Linked Field',
        flex: 1,
        renderCell: ({ row }) => (
          <div className={classes.label}>
            {row.linkedField && linkedFieldOptions[row.linkedField]}
          </div>
        ),
      },
      {
        field: 'action',
        flex: 1,
        headerName: '',
        renderCell: ({ row }) => {
          return (
            <Button onClick={() => setEditingDocumentTag(row)}>
              <EditIcon />
            </Button>
          );
        },
      },
    ];

    return gridColumns;
  }, [classes.label]);

  const [createDocumentTag, { loading: createDocumentTagExecuting }] =
    useDocumentTag_CreateDocumentTagMutation({
      onCompleted: () => {
        setIsAddDocumentTagModalOpen(false);
        refetchDocumentTags();
        showToast({
          message: 'Document tag created successfully',
          severity: NotificationSeverity.SUCCESS,
        });
      },
      onError: (e) => {
        showAlert({
          message: 'Error creating document tag: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const [updateDocumentTag, { loading: updateDocumentTagExecuting }] =
    useDocumentTag_UpdateDocumentTagMutation({
      onCompleted: () => {
        setEditingDocumentTag(null);
        refetchDocumentTags();
        showToast({
          message: 'Document tag update successfully',
          severity: NotificationSeverity.SUCCESS,
        });
      },
      onError: (e) => {
        showAlert({
          message: 'Error updating document tag: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const handleCreateDocumentTag = (values: DocumentTag_CreateDocumentTagInput) => {
    createDocumentTag({
      variables: {
        input: values,
      },
    });
  };

  const handleUpdateDocumentTag = (input: DocumentTag_UpdateDocumentTagInput) => {
    updateDocumentTag({
      variables: {
        input,
      },
    });
  };

  return (
    <HomeLayout showPortalName={false}>
      <div className={classes.container}>
        <div style={{ display: 'flex', verticalAlign: 'bottom' }}>
          <Typography className={classes.title} variant="h5" style={{ flex: 3 }}>
            <span onClick={() => history.push(`/home`)}>
              <span className={classes.routeIconContainer}>
                <img src="/home-icon.svg" alt="home-icon" />
              </span>
              <span className={classes.routeIconContainer}>
                <ChevronRight />
              </span>
            </span>
            Document Tags
          </Typography>
        </div>
        <div>
          <Button
            variant="contained"
            type="button"
            className={classes.button}
            onClick={() => setIsAddDocumentTagModalOpen(true)}
          >
            Add a new document tag
          </Button>
        </div>
        <DataGrid
          loading={loadingDocumentTags && !documentTagsData}
          className={classes.root}
          columns={columns}
          rows={documentTagsData?.data || []}
          disableColumnReorder
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          autoHeight={true}
          rowHeight={70}
          pagination
        />
        {isAddDocumentTagModalOpen && (
          <CreateDocumentTagModal
            createDocumentTagExecuting={createDocumentTagExecuting}
            onClose={() => setIsAddDocumentTagModalOpen(false)}
            onSubmit={handleCreateDocumentTag}
          />
        )}
        {editingDocumentTag && (
          <EditDocumentTagModal
            documentTag={editingDocumentTag}
            updateDocumentTagExecuting={updateDocumentTagExecuting}
            onClose={() => setEditingDocumentTag(null)}
            onSubmit={handleUpdateDocumentTag}
          />
        )}
      </div>
    </HomeLayout>
  );
};

export default SuperAdminDocumentTags;
